"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Scale, Eye, ClipboardCheck, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const tasks = [{
  title: "Memberikan pertimbangan organisasi",
  icon: Scale,
  description: "Memberikan masukan dan pertimbangan strategis untuk kemajuan organisasi."
}, {
  title: "Pengawasan organisasi",
  icon: Eye,
  description: "Melakukan fungsi kontrol dan pengawasan terhadap jalannya roda organisasi."
}, {
  title: "Evaluasi organisasi",
  icon: ClipboardCheck,
  description: "Mengevaluasi kinerja dan program kerja yang telah dilaksanakan."
}, {
  title: "Pendampingan organisasi",
  icon: Users,
  description: "Memberikan pendampingan dalam penyelesaian dinamika internal organisasi."
}];
export default function MajelisPertimbanganOrganisasi() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="mpo" className="bg-background py-16 md:py-24 overflow-hidden">
      <div ref={ref} className={`container mx-auto px-4 md:px-6 max-w-5xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-6 uppercase tracking-tight">
            Majelis Pertimbangan Organisasi
          </h2>
          <div className="h-1.5 w-24 bg-primary mx-auto rounded-md"></div>
        </div>

        <div className="space-y-12">
          <div className="scroll-mt-24">
            <Card className="border-border shadow-lg bg-card">
              <CardHeader className="text-center pb-4 md:pb-8 border-b border-border/50">
                <CardTitle className="text-2xl md:text-3xl font-default text-secondary">
                  SAPMA - Tugas Pokok & Fungsi
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-8 md:pt-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  {tasks.map((task, i) => <div key={i} data-index={i} className="flex items-start gap-5 p-6 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/40 hover:bg-muted/50 transition-all duration-300 group">
                      <div className="bg-primary/10 p-4 rounded-lg text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <task.icon className="w-7 h-7" />
                      </div>
                      <div className="flex flex-col justify-center">
                        <h3 className="font-semibold font-default text-foreground text-lg md:text-xl leading-tight mb-2">
                          {task.title}
                        </h3>
                        <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                          {task.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="scroll-mt-24">
            <Card className="border-border shadow-lg bg-card">
              <CardHeader className="text-center pb-4 md:pb-8 border-b border-border/50">
                <CardTitle className="text-2xl md:text-3xl font-default text-secondary">
                  SRIKANDI - Tugas Pokok & Fungsi
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-8 md:pt-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  {tasks.map((task, i) => <div key={i} data-index={i} className="flex items-start gap-5 p-6 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/40 hover:bg-muted/50 transition-all duration-300 group">
                      <div className="bg-primary/10 p-4 rounded-lg text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <task.icon className="w-7 h-7" />
                      </div>
                      <div className="flex flex-col justify-center">
                        <h3 className="font-semibold font-default text-foreground text-lg md:text-xl leading-tight mb-2">
                          {task.title}
                        </h3>
                        <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                          {task.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="scroll-mt-24">
            <Card className="border-border shadow-lg bg-card">
              <CardHeader className="text-center pb-4 md:pb-8 border-b border-border/50">
                <CardTitle className="text-2xl md:text-3xl font-default text-secondary">
                  KOTI - Tugas Pokok & Fungsi
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-8 md:pt-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  {tasks.map((task, i) => <div key={i} data-index={i} className="flex items-start gap-5 p-6 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/40 hover:bg-muted/50 transition-all duration-300 group">
                      <div className="bg-primary/10 p-4 rounded-lg text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <task.icon className="w-7 h-7" />
                      </div>
                      <div className="flex flex-col justify-center">
                        <h3 className="font-semibold font-default text-foreground text-lg md:text-xl leading-tight mb-2">
                          {task.title}
                        </h3>
                        <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                          {task.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}